import { Switch } from '@headlessui/react'
import { Popup } from '@progress/kendo-react-popup'
import { GlobalDialogType } from 'components/Modals/GlobalDialog'
import InputSpinner from 'components/Spinners/input'
import Translation from 'components/Translation'
import { useAppContext } from 'context/AppContext'
import { useAppErrorApiContext } from 'context/AppErrorContext'
import { MouseEventHandler, useRef, useState } from 'react'
import { sendBookingNotification } from 'services/notifications'
import { AttendeeItem } from 'types/attendee'
import { CalendarSlotItem } from 'types/calendar'
import { MembershipItem } from 'types/membership'
import { Order } from 'types/order'
import { classNames } from 'utils/dom-helpers'
import {
    putApiRequest,
    useApiRequest,
} from 'utils/fetch-helper'
import { getTranslation } from 'utils/language-helper'
import {
    parseISOString,
    toCurrency,
} from 'utils/string-localization-helper'
import { useAuth } from '../../context/AuthContext'
import AmountPaid from './AmountPaid'
import getConfig from 'next/config'

export function AttendeeListItem(props: {
    attendee: AttendeeItem
    data: CalendarSlotItem
    expand: MouseEventHandler<HTMLDivElement | HTMLButtonElement>
    is_expanded?: boolean
    cancelAppointment: (id: any) => void
    block_ux?: boolean
    notifyAttendeeUpdatedSuccessfully: (attendee_name: string) => void
    is_edit_disabled?: boolean
    is_disable_others?: boolean
    handleShowResponses?: () => void
    has_forms?: boolean
}) {
    const { getApiRequest } = useApiRequest()
    const { setGlobalError } = useAppErrorApiContext()
    const { CreateClientModal, GlobalDialog } = useAuth()
    const { setGlobalLoading, locale, translations } = useAppContext()

    const {
        attendee,
        data,
        expand,
        is_expanded,
        notifyAttendeeUpdatedSuccessfully,
        is_disable_others,
        handleShowResponses,
        has_forms
    } = props

    const icon_ref = useRef<HTMLInputElement>(null)
    const [show_client_details, setShowClientDetails] = useState(false)
    const [membership, setMembership] = useState<MembershipItem>()
    const [is_loading_membership, setLoadingMembership] = useState(false)
    const [disable_coupon, setDisableCoupon] = useState(true)
    const [coupon, setCoupon] = useState(attendee?.order?.coupon?.code)
    const [signed_in_at, setSignedInAt] = useState<Date | undefined>(
        attendee?.signed_in_at
            ? new Date(parseISOString(attendee.signed_in_at))
            : undefined
    )
    const [notes, setNotes] = useState<string>(attendee?.notes || '')
    const [is_saving, toggleSaving] = useState<boolean>(false)
    const { NEXT_PUBLIC_STATIC_DOMAIN } = getConfig().publicRuntimeConfig

    const openClientModal = () => {
        if (!props.is_edit_disabled) {
            CreateClientModal.setAttributes({
                email: attendee.user?.email,
            })
            CreateClientModal.open()
        }
    }

    const handleDiscount = () => {
        if (!attendee?.order)
            return {
                is_free_appointment: true,
                is_membership_appointment: false,
                fully_paid: true,
            }

        const { discount, fully_paid, membership_client_id } =
            attendee?.order as Order
        const is_free_appointment =
            (!fully_paid && discount && discount?.amount) == 100 || 0
        const is_membership_appointment = !!membership_client_id

        async function getMembership(membership_client_id: string) {
            setLoadingMembership(true)
            const membership_client = await getApiRequest(
                `/v1/memberships/clients/${membership_client_id}`
            )
            if (membership_client.membershipId) {
                const membership = await getApiRequest(
                    `/v1/memberships/by-id?id=${membership_client.membershipId}`
                )
                if (membership.name) {
                    setMembership(membership)
                }
            }
        }

        if (membership_client_id && !membership && !is_loading_membership) {
            getMembership(membership_client_id)
        }
        return {
            is_free_appointment,
            is_membership_appointment,
            fully_paid,
        }
    }
    const { is_free_appointment, is_membership_appointment, fully_paid } =
        handleDiscount()

    async function onSubmit() {
        try {
            toggleSaving(true)
            const payload = {
                notes: notes,
                signedInAt: signed_in_at
                    // TODO: refactor this with BE
                    ? signed_in_at.toISOString().replace('Z', '')
                    : undefined,
            }
            let res = await putApiRequest(
                `/v1/bookings/${attendee?.booking_id || data?.booking_id}`,
                payload
            )
            if (res?.ok) {
                notifyAttendeeUpdatedSuccessfully(
                    `${attendee.user?.firstName} ${attendee.user?.lastName}`
                )
            } else {
                throw res
            }
        } catch (e) {
            setGlobalError(e)
        } finally {
            toggleSaving(false)
        }
    }

    const handleResend = async () => {
        try {
            setGlobalLoading(true)
            const res = await sendBookingNotification(attendee?.booking_id || data?.booking_id as string, attendee.id)
            if (res?.ok) {
                GlobalDialog.setAttributes({
                    title: 'common.resend_success_title',
                    message: attendee?.user?.email,
                    className: 'w-3/4 sm:w-1/2 overflow-hidden',
                    type: GlobalDialogType.SUCCESS,
                })
                GlobalDialog.open()
            }
            else {
                throw res
            }
        } catch (e) {
            setGlobalError(e)
        } finally {
            setGlobalLoading(false)
        }
    }

    return (
        <div
            key={attendee.id}
            className="flex flex-col"
            data-component="AttendeeListItem"
        >
            <div
                className={classNames(
                    is_disable_others
                        ? 'bg-gray-500/20 rounded-lg cursor-not-allowed'
                        : 'hover:text-primary-light bg-[background: #FAFCFE]',
                    'flex justify-between items-center py-3 gap-4 px-2'
                )}
                onMouseEnter={() => {
                    if (!props.is_edit_disabled) {
                        icon_ref.current?.classList.remove('sm:invisible')
                    }
                }}
                onMouseLeave={() => {
                    if (!props.is_edit_disabled) {
                        !icon_ref.current?.classList.contains('sm:invisible')
                            ? icon_ref.current?.classList.add('sm:invisible')
                            : ''
                    }
                }}
            >
                <div
                    className={classNames(
                        is_disable_others ? '' : 'cursor-pointer',
                        'h-8 flex items-center'
                    )}
                    onClick={expand}
                >
                    <div className='flex flex-row'>
                        <Translation
                            className="mr-1"
                            render_as="span"
                            content_key="locale_fullname"
                            content_values={{
                                first_name: attendee.user?.firstName,
                                last_name: attendee.user?.lastName,
                            }}
                        />
                        <span
                            className="feather feather-external-link text-primary-light sm:invisible mr-2"
                            ref={icon_ref}
                            onClick={openClientModal}
                            onMouseEnter={() => setShowClientDetails(true)}
                            onMouseLeave={() => setShowClientDetails(false)}
                        />
                        <PaymentStatus order={attendee.order} />
                    </div>

                    <Popup
                        anchor={icon_ref.current}
                        show={
                            show_client_details && !props.is_edit_disabled
                        }
                        animate={false}
                        popupAlign={{
                            horizontal: 'left',
                            vertical: 'top',
                        }}
                        popupClass={
                            'bg-black text-white text-xs shadow-lg rounded-xl w-auto px-2 py-1 mt-2'
                        }
                        className="z-[102]"
                    >
                        <Translation
                            render_as="p"
                            content_key="view_client_details"
                        />
                    </Popup>
                </div>
                <div className="flex gap-3">
                    {fully_paid ? (
                        <div className="flex items-center gap-1">
                            <i className="feather-check text-green-500" />
                            <Translation content_key="paid" />
                        </div>
                    ) : (
                        ''
                    )}
                    <img
                        src={
                            signed_in_at
                                ? `https://${NEXT_PUBLIC_STATIC_DOMAIN}/images/availability/ico-circle-green.svg`
                                : `https://${NEXT_PUBLIC_STATIC_DOMAIN}/images/availability/ico-circle-red.svg`
                        }
                        alt={`Availability Icon ${signed_in_at ? 'Green' : 'Red'
                            }`}
                        title={
                            signed_in_at
                                ? `${getTranslation(
                                    'calendar.checked_in_at',
                                    translations
                                )} ${signed_in_at.toLocaleString(locale, {
                                    weekday: 'short',
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                    hour: 'numeric',
                                    minute: 'numeric',
                                })}`
                                : getTranslation(
                                    'calendar.not_checked_in',
                                    translations
                                )
                        }
                    />
                    <div className='flex gap-1'>
                        {has_forms &&
                            <button
                                className='rounded-md bg-blue-50 bg-opacity-40 w-7 h-7 disabled:cursor-not-allowed disabled:opacity-50'
                                title={getTranslation('forms.view_responses', translations)}
                                disabled={!handleShowResponses}
                                onClick={handleShowResponses}
                            >
                                <i className="feather-clipboard text-sm mt-1 text-secondary-blue" />
                            </button>
                        }

                        <button
                            className='rounded-md bg-red-200 bg-opacity-40 w-7 h-7'
                            title={getTranslation('common.resend_title', translations)}
                            onClick={handleResend}
                        >
                            <i className="feather-mail text-sm mt-1 text-red-500" />
                        </button>
                        <button
                            className={classNames(
                                'rounded-md bg-gray-200 bg-opacity-40 w-7 h-7',
                                is_disable_others ? 'cursor-not-allowed' : ''
                            )}
                            onClick={expand}
                        >
                            <i className="feather-edit text-sm mt-1 text-gray-900" />
                        </button>
                    </div>
                </div>
            </div>
            <div
                className={classNames(
                    is_expanded ? 'p-3 rounded-lg mt-2' : 'hidden',
                    'flex flex-col bg-[#F2F3F4] text-gray-800 gap-5'
                )}
            >
                <div className="flex gap-6">
                    <button
                        className="disabled:bg-red-100 disabled:text-red-200 bg-red-500 bg-opacity-10 text-red-600 flex gap-3 items-center rounded-lg aot-btn w-fit  justify-center"
                        disabled={props.block_ux}
                        onClick={() =>
                            props.cancelAppointment(attendee?.id as string)
                        }
                    >
                        <i className="feather-trash-2 " />
                        <Translation content_key="cancel_appointment" />
                    </button>
                </div>

                <div>
                    <Translation
                        render_as="div"
                        className="text-lg font-semibold text-primary"
                        content_key="payment"
                    />
                    <div className="flex justify-between pb-1">
                        <Translation
                            render_as="div"
                            content_key="total_price"
                        />
                        <div>
                            {data.price ? toCurrency(data.price) : ''}
                        </div>
                    </div>
                    <AmountPaid
                        order={attendee.order}
                        price={data.price}
                        service_id={data.service_id}
                        email={attendee?.user?.email}
                        group_class_availability_id={data.availability_id}
                    />
                </div>

                <div className="flex">
                    <Switch
                        checked={!!signed_in_at}
                        onChange={() =>
                            setSignedInAt(
                                !!signed_in_at ? undefined : new Date()
                            )
                        }
                        className={classNames(
                            !!signed_in_at ? 'bg-primary' : 'bg-gray-300',
                            `block relative inline-flex flex-shrink-0 h-7 w-12
                            border-2 border-transparent rounded-full cursor-pointer
                            transition-colors ease-in-out duration-200 focus:outline-none 
                            focus-visible:ring-2  focus-visible:ring-white focus-visible:ring-opacity-75`
                        )}
                    >
                        <span
                            aria-hidden="true"
                            className={classNames(
                                !!signed_in_at
                                    ? 'translate-x-5'
                                    : 'translate-x-0',
                                `pointer-events-none inline-block h-6 w-6 rounded-full 
                                    bg-white shadow-lg transform ring-0 transition ease-in-out duration-300`
                            )}
                        />
                    </Switch>
                    <div className="inline-flex ml-2 mt-1.5">
                        {!!signed_in_at ? (
                            <>
                                <Translation
                                    content_key="calendar.checked_in_at"
                                    render_as="label"
                                />
                                &nbsp;
                                <label className="font-bold">
                                    {signed_in_at.toLocaleString(locale, {
                                        weekday: 'short',
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric',
                                        hour: 'numeric',
                                        minute: 'numeric',
                                    })}
                                </label>
                            </>
                        ) : (
                            <Translation content_key="calendar.check_in" />
                        )}
                    </div>
                </div>
                <div
                    className={classNames(
                        !coupon &&
                            (is_membership_appointment ||
                                is_free_appointment)
                            ? 'hidden'
                            : ''
                    )}
                >
                    <label htmlFor="details" className="aot-form-label">
                        <Translation content_key="coupon" />
                    </label>
                    <div className="relative editattendlist">
                        <input
                            type="text"
                            disabled={true} //TODO: change to use disable_coupon when ready
                            id="details"
                            className={classNames(
                                ' focus:ring-primary-light focus:border-primary-light block w-full  border-gray-300 rounded-md',
                                // coupon ? "" : TODO: comment out when ready
                                'bg-gray-200'
                            )}
                            value={
                                coupon ||
                                getTranslation(
                                    'calendar.coupon_not_applied',
                                    translations
                                )
                            }
                        />
                        <div
                            className="bg-red-100 py-1 px-3 text-red-500 rounded-md absolute top-3 right-3 cursor-pointer hidden" // TODO: remove hidden when ready
                            onClick={() => {
                                setDisableCoupon(false)
                            }}
                        >
                            <i className="feather-edit mr-1" />
                            <Translation content_key="change" />
                        </div>
                    </div>
                </div>

                <div className='editattendlist'>
                    <label htmlFor="details" className="aot-form-label">
                        <Translation content_key="membership" />
                    </label>
                    <div className="bg-gray-200 border-gray-300 rounded-md  text-base edit-membership">
                        {membership?.name ? (
                            membership.name
                        ) : (
                            <Translation content_key="no_applicable_membership" />
                        )}
                    </div>
                </div>
                <div>
                    <Translation
                        content_key="notes"
                        render_as="label"
                        className="aot-form-label"
                    />
                    <textarea
                        id="notes"
                        className={classNames(
                            'placeholder-gray-300  h-40 focus:ring-primary-light focus:border-primary-light block w-full  border-gray-300 rounded-md px-6 py-3'
                        )}
                        placeholder={getTranslation(
                            'calendar.add_notes_here',
                            translations
                        )}
                        value={notes}
                        onChange={(e) => {
                            setNotes(e.target.value)
                        }}
                    />
                </div>
                <div className="flex flex-row gap-4">
                    <button
                        type="button"
                        className="border border-gray-300 rounded-lg aot-btn"
                        onClick={expand}
                    >
                        <Translation content_key="close" />
                    </button>
                    <button
                        disabled={is_saving}
                        type="button"
                        onClick={() => {
                            onSubmit()
                        }}
                        className={classNames(
                            'group relative flex justify-center',
                            'aot-btn border border-transparent',
                            'rounded-md text-white',
                            'focus:outline-none',
                            ' focus:ring-2 focus:ring-offset-2 focus:ring-primary-light',
                            'bg-primary-dark'
                        )}
                    >
                        <InputSpinner loading={is_saving} />
                        <Translation
                            content_key={
                                is_saving ? 'calendar.saving' : 'save'
                            }
                        />
                    </button>
                </div>
            </div>
        </div>
    )
}

export function PaymentStatus({ order }: { order?: Order }) {
    const { translations } = useAppContext()
    if (order) {
        const { coupon, fully_paid, membership_client_id, total_amount } =
            order

        // Membership
        if (membership_client_id) {
            return (
                <Translation
                    content_key="member"
                    render_as='span'
                    className='flex bg-green-100 text-green-900 text-center items-center px-4 py-1 text-xs font-bold rounded-full uppercase w-fit'
                />
            )
        }

        // Paid with Coupons
        if (coupon?.code) {
            return (
                <span
                    title={getTranslation(
                        'calendar.coupon_applied',
                        translations
                    )}
                    className='flex bg-green-500 text-white px-4 py-1 text-xs text-center items-center font-bold rounded-full uppercase w-fit'

                >
                    {coupon.code}
                </span>
            )
        }

        // Free Appointments
        if (!fully_paid && total_amount == 0) {
            return (
                <Translation
                    content_key="free"
                    render_as='span'
                    className='flex bg-green-100 text-green-900 px-4 py-1 inline-flex text-xs text-center items-center font-bold rounded-full uppercase w-fit'
                />
            )
        }
    }
    return <></>
}
